import React from 'react';
import PropTypes from 'prop-types';
import Menu from '@mui/material/Menu';

/**
 * Custom Menu component with init children with props:
 *
 * @param {Object} props - Object. Component props.
 * @param {Node} props.children - Node. Children components.
 * @param {Boolean} props.open - Boolean. Flag indicating that the Menu is open.
 * @param {Object} props.anchor - Object. Contains anchor ref.
 * @param {Function} props.close - Function. Executed on the Menu closure event.
 * @param {Object} props.anchorOrigin Object. The position on the anchor where the popover's anchorEl will attach to.
 * @param {Object} props.transformOrigin - Object. The position on the popover which will attach to the anchor's origin.
 * @param {Object} props.styles - Object. Contains the Menu css styles.
 * @param {Object} props.rootStyle - Object. Contains the Popover component's css styles.
 * @param {String} props.beforePosition - String. Can be 'Left' or 'Right' and indicates where to put the before div.
 *
 * @returns {React.JSX.Element}
 */

const CustomMenu = function CustomMenu({
  open,
  anchor = null,
  close = () => {},
  styles = null,
  rootStyle = null,
  transformOrigin = { horizontal: 'right', vertical: 'top' },
  anchorOrigin = { horizontal: 'right', vertical: 'bottom' },
  children,
  ...additionalProps
}) {
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { onClose: close, ...additionalProps });
    }
    return child;
  });

  return (
    <Menu
      anchorEl={anchor}
      id="custom-menu"
      open={open}
      onClose={close}
      style={styles}
      PopoverClasses={rootStyle}
      transformOrigin={transformOrigin}
      anchorOrigin={anchorOrigin}
    >
      {childrenWithProps}
    </Menu>
  );
};

CustomMenu.propTypes = {
  open: PropTypes.bool.isRequired,
  close: PropTypes.func,
  anchor: PropTypes.object,
  styles: PropTypes.object,
  rootStyle: PropTypes.object,
  anchorOrigin: PropTypes.object,
  beforePosition: PropTypes.string,
  transformOrigin: PropTypes.object,
  children: PropTypes.node.isRequired,
};

export default CustomMenu;
